/**
 * SegmentFillPanel - the Segment fill-type editor (WL-012, deck `segment-fills`).
 *
 * The spine of Segment styling: a fill-TYPE selector (Color / Gradient / Pattern
 * / Image / GIF) plus the sub-panel for the active type, living in the Edit-Wheel
 * SIDE PANE (SIDE-PANE LAW, decisions-log row 29 - never inside the Segment).
 *
 * Presentational + controlled: it owns no server state. The parent passes the
 * current `fill` and an `onChange(nextFill)` callback (wire to the existing
 * `updateSegment*` Redux path at integration - see PR note). The live preview
 * uses the pure `buildSegmentFillCss` so what you see is exactly what renders.
 *
 * Composes WL-001 tokens (src/theme/tokens). Reduced-motion aware for GIF.
 * Accessibility: the type selector is a real ARIA `tablist`, each preview swatch
 * has an accessible name, and every control is keyboard reachable.
 */

import React, { useMemo } from "react";
import { tokens } from "../../../../theme/tokens";
import {
  FILL_TYPES,
  FILL_TYPE_LABEL,
  buildSegmentFillCss,
  defaultSegmentFill,
} from "./logic/segmentFill";
import useSegmentFillAnimation from "./useSegmentFillAnimation";
import FocalZoomControl from "./FocalZoomControl";
import { SEGMENT_ARC_FILLS_ENABLED } from "../../../D3Wheels/segmentArcFillsFlag";
import { extractGifPoster } from "./logic/gifPoster";
import { gradientStateFromColor } from "../../../gradientBuilder/gradientCss";
// REUSE, NOT DUPLICATION: the Image / GIF sub-editors are the app's existing
// media picker (Upload / Embed URL / Browse / Recent + isGifAllowed), the same
// one the legacy Wallpaper accordion uses. The Pattern sub-editor is the existing
// self-contained SVG-pattern picker (REACT_APP_BG_PATTERNS work). No new grids,
// no new upload pipeline — the panel stays controlled and emits its choice via
// `onChange` (the parent maps it onto the live redux/save path).
// parsePatternCss is a pure helper (no heavy deps) — safe to import eagerly.
import { parsePatternCss } from "../../../backgroundPatterns/toBackgroundCss";

// The Image/GIF media picker and the SVG-pattern picker are lazy-loaded: their
// subtrees are heavy and browser-only (upload/HEIC/Web-Worker pipeline; MUI +
// rc-slider CSS), so deferring them keeps this panel importable in lightweight
// contexts and only loads a picker when the user actually opens that fill tab.
const MediaSelection = React.lazy(() => import("../MediaSelection"));
const BackgroundPatternPanel = React.lazy(() =>
  import("../../../backgroundPatterns").then((m) => ({
    default: m.BackgroundPatternPanel,
  }))
);

const PL = "#9B5AF8";
const INK = "#123051";
const SUB = "#565556";
const LINE = "#e1e1e1";

const useReducedMotion = () => {
  const [reduced, setReduced] = React.useState(false);
  React.useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const on = () => setReduced(mq.matches);
    on();
    mq.addEventListener ? mq.addEventListener("change", on) : mq.addListener(on);
    return () =>
      mq.removeEventListener ? mq.removeEventListener("change", on) : mq.removeListener(on);
  }, []);
  return reduced;
};

// REACT_APP_BUILDER_SIZING_FIX (default OFF, byte-identical): the color swatches
// were sized 44x44 as a WCAG 2.5.5 touch target (WL-027). Gordon 2026-07-13 ruling:
// the web Builder is click-driven, not touch — the 44px touch-target floor does not
// apply on desktop web, so the swatch renders at its design size (32px) there. The
// a11y intent is kept alive in the responsive/touch layer: on a coarse pointer or a
// narrow (mobile) width we restore the 44px tap area. When the flag is OFF the swatch
// stays 44 unconditionally, so the rendered output is byte-identical to before.
const BUILDER_SIZING_FIX = process.env.REACT_APP_BUILDER_SIZING_FIX === "true";
const SWATCH_DESKTOP_PX = 32; // design/app-real size (WL-027 follow-up)
const SWATCH_TOUCH_PX = 44; // WCAG 2.5.5 touch target, kept for coarse-pointer / narrow widths

// True on touch/coarse pointers or narrow (mobile) viewports — where the larger tap
// target is still warranted. Mirrors the useReducedMotion matchMedia pattern above.
const useTouchSizing = () => {
  const [touch, setTouch] = React.useState(false);
  React.useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const mq = window.matchMedia("(pointer: coarse), (max-width: 640px)");
    const on = () => setTouch(mq.matches);
    on();
    mq.addEventListener ? mq.addEventListener("change", on) : mq.addListener(on);
    return () =>
      mq.removeEventListener ? mq.removeEventListener("change", on) : mq.removeListener(on);
  }, []);
  return touch;
};

// Gordon 2026-08-19 (register 508): the "Segment Fill" section read off-brand
// against the rest of the side pane on THREE measured axes — width, tabs, and
// swatch size. Fixed here (CSS-only; the tablist keeps its existing DOM/roving-
// tabIndex contract so SegmentFillPanel.tablist.test.jsx's a11y assertions stay
// intact, per the app's "reuse the shared Tabs *look*, don't break a tested a11y
// contract to force the shared Tabs *component*" call):
//   - panel: dropped the fixed maxWidth:360 card so it spans the ToolAccordion's
//     full width (AccordionDetails has padding:0 — see ToolAccordion.styled.js)
//     instead of floating a narrower box inside it.
//   - tab: dropped the filled/bordered "pill" look for the app-standard text +
//     underline pattern (see src/components/Tabs/styles.js's TabIndicator), and
//     each tab now shares the row's width equally (flex:1) instead of hugging
//     content, so the row itself reads as full-width tabs, not a pill cluster.
//   - preview: 56px was a one-off — every OTHER side-pane swatch/icon-preview in
//     this app (Wallpaper.styled.jsx SwatchOverlayImage/SwatchBackgroundImage/
//     StyledRightIconContainer) is 32px. Matched to 32 for on-brand consistency.
const styles = {
  panel: {
    fontFamily: tokens.fontFamily,
    color: INK,
    width: "100%",
    boxSizing: "border-box",
  },
  h: { margin: 0, fontSize: 14, fontWeight: tokens.fontWeight.bold },
  sub: { margin: `${tokens.spacing.xs}px 0 ${tokens.spacing.md}px`, fontSize: 12, color: SUB },
  tablist: { display: "flex", width: "100%", borderBottom: `1px solid ${LINE}`, marginBottom: tokens.spacing.md },
  tab: (active) => ({
    flex: 1,
    border: "none",
    borderBottom: `2px solid ${active ? PL : "transparent"}`,
    background: "transparent",
    color: active ? PL : SUB,
    borderRadius: 0,
    padding: "10px 8px",
    minHeight: 44, // WCAG 2.5.5 touch target (WL-027)
    font: `600 12.5px ${tokens.fontFamily}`,
    cursor: "pointer",
  }),
  previewWrap: { display: "flex", alignItems: "center", gap: tokens.spacing.md, marginBottom: tokens.spacing.md },
  preview: (bg) => ({
    width: 32,
    height: 32,
    borderRadius: tokens.radius.round,
    border: `1px solid ${LINE}`,
    background: bg,
    flex: "0 0 auto",
  }),
  chip: {
    fontSize: 10,
    fontWeight: 700,
    color: "#6f27d6",
    background: "#F1E9FE",
    borderRadius: 6,
    padding: "1px 6px",
  },
  swatchRow: (px = 44) => ({ display: "grid", gridTemplateColumns: `repeat(auto-fill, ${px}px)`, gap: tokens.spacing.sm }),
  swatch: (c, sel, px = 44) => ({
    width: px, // 44 = WCAG 2.5.5 touch target (WL-027); 32 = design size on desktop web (flag on)
    height: px,
    borderRadius: tokens.radius.round,
    background: c,
    border: sel ? `2px solid ${PL}` : `1px solid ${LINE}`,
    cursor: "pointer",
    padding: 0,
  }),
  hint: { fontSize: 11, color: SUB, marginTop: tokens.spacing.sm },
};

const PRESET_COLORS = ["#9B5AF8", "#6D5EF6", "#34D399", "#F59E0B", "#F472B6", "#22D3EE", "#123051", "#FFFFFF"];

const SegmentFillPanel = ({
  fill: fillProp,
  onChange = () => {},
  segmentName = "Segment",
  open = false,
  // Additive (u6-segment-fill): when provided, this node REPLACES the native
  // preset-color swatch grid in the Color sub-panel, so a host can re-house the
  // app's rich <SegmentColor> palette (Preset / Custom / Wheel / Segment) into
  // this panel's Color tab without forking the tab strip. Default undefined →
  // byte-identical to before (the native swatches render).
  colorPanel,
  // Additive (ISP-9999 accordion-settings-cleanup): lets a non-Segment host
  // (e.g. the Accordion block's Title Background) reuse this exact control
  // under its own heading/copy instead of the literal "Segment fill" text.
  // Defaults preserve the original strings byte-identical for every existing
  // Segment Fill caller.
  heading = "Segment fill",
  subheading = "Choose how this Segment is filled. Edited here in the side pane.",
}) => {
  const fill = fillProp || defaultSegmentFill();
  // Latest-fill ref so an async GIF-poster callback merges onto the current fill
  // (not the value captured when the pick started) — avoids a stale-closure clobber.
  const fillRef = React.useRef(fill);
  fillRef.current = fill;
  const reducedMotion = useReducedMotion();
  const touchSizing = useTouchSizing();

  // Local upload/browse UI state for the media pickers (Upload + Embed URL need a
  // media buffer). Shared across the Image/GIF tabs — only one renders at a time.
  const [media, setMedia] = React.useState(null);

  // Normalise whatever the media picker hands back (string URL, or an object with
  // src/url/file) into a plain URL string.
  const extractUrl = (value) => {
    if (!value) return "";
    if (typeof value === "string") return value;
    return value.src || value.url || value.file || value.path || "";
  };

  // Apply a picked Image / GIF: set the V2 fill blob (drives this panel's preview
  // + a11y chip) AND let the parent map it onto the live segment-fill fields.
  const applyMedia = (kind) => (_type, value) => {
    const url = extractUrl(value);
    if (!url) return;
    const key = kind === "gif" ? "gif" : "image";
    onChange({ ...fill, type: kind, [key]: { ...(fill[key] || {}), url } });

    // EW-003 u1b: on GIF selection, extract + store the first-frame POSTER so the
    // closed wedge + reduced-motion surfaces show a real still (the open frame
    // animates the live GIF). Async + best-effort: a failure resolves to null and
    // the surfaces fall back to the GIF url as its own still. Flag-gated so the
    // OFF path is byte-identical.
    if (kind === "gif" && SEGMENT_ARC_FILLS_ENABLED) {
      extractGifPoster(url).then((poster) => {
        if (!poster) return;
        onChange({
          ...fillRef.current,
          type: "gif",
          gif: { ...((fillRef.current && fillRef.current.gif) || {}), url, poster },
        });
      });
    }
  };

  const clearMedia = (kind) => () => {
    const key = kind === "gif" ? "gif" : "image";
    onChange({ ...fill, type: kind, [key]: {} });
  };

  // EW-003 u1b: non-destructive crop — store focal point + zoom + display mode on
  // the active media node. Both surfaces (wedge <pattern> / open frame) read these
  // at render time; nothing is baked. Flag-gated (control only renders when ON).
  const applyCrop = (kind) => ({ focus, mode }) => {
    const key = kind === "gif" ? "gif" : "image";
    onChange({ ...fill, type: kind, [key]: { ...(fill[key] || {}), focus, mode } });
  };

  // Apply a picked Pattern: BackgroundPatternPanel emits a finished CSS string;
  // keep it (for the live apply path) plus the recovered descriptor (for the
  // canonical buildSegmentFillCss preview).
  //
  // The "None" tile (BackgroundPatternPanel.handleClear) emits onApply("") to
  // explicitly clear an already-chosen pattern. That must commit a fresh EMPTY
  // pattern object, not `{...fill.pattern, ..., css: ""}` — the old spread kept
  // the STALE `id`/`fg`/`bg` from the pattern being cleared, so
  // buildSegmentFillCss/legacyFieldsForFill (both id-driven, not css-driven —
  // see logic/legacyFillFields.js) silently reconstructed the SAME old pattern
  // and "None" was a no-op (live-reproduced on wheel 5578 seg 42100: clicking
  // None showed the checkmark but segment_bg_color never changed). An empty
  // `pattern: {}` is the same explicit-clear shape `clearMedia()` already
  // commits for Image/GIF (`image: {}` / `gif: {}`), so legacyFieldsForFill can
  // tell "explicitly cleared" apart from "tab switched, nothing picked yet"
  // (`fill.pattern` entirely absent).
  const applyPattern = (css) => {
    if (!css) {
      onChange({ ...fill, type: "pattern", pattern: {} });
      return;
    }
    const desc = parsePatternCss(css) || {};
    onChange({ ...fill, type: "pattern", pattern: { ...(fill.pattern || {}), ...desc, css } });
  };

  // Flag OFF => 44 unconditionally (byte-identical). Flag ON => 32 on desktop web,
  // but keep the 44 tap target on coarse pointers / narrow (mobile) widths.
  const swatchPx = BUILDER_SIZING_FIX && !touchSizing ? SWATCH_DESKTOP_PX : SWATCH_TOUCH_PX;

  const bg = useMemo(
    () => buildSegmentFillCss(fill, { open, reducedMotion }),
    [fill, open, reducedMotion]
  );

  // WL-058 render-side wedge animation (de-flagged 2026-07-09 — now the only
  // design). Drives a GPU-only transform pan on the preview swatch while a GIF
  // Segment is open. The reduced-motion (matchMedia) + offscreen-pause
  // (IntersectionObserver) guardrails live in the hook and are driven by the real
  // signals, NOT a feature flag — so a GIF fill still rests on its poster frame
  // when the viewer prefers reduced motion or the Segment scrolls offscreen.
  const fillAnim = useSegmentFillAnimation({ fill, open });
  const animateSwatch = fill.type === "gif";

  // gordon-0925-P1-segment-fill-gradient-and-pattern-do-not-persist: switching
  // TO the Gradient tab immediately paints the default Linear 2-stop gradient
  // in both this panel's own preview swatch AND GradientBuilderPanel's preview
  // (buildSegmentFillCss/buildGradientCss fall back to gradientCss.js's own
  // defaultGradientState() when `fill.gradient` is absent) — so a user who
  // never touches a stop/angle handle sees exactly what they'd expect from
  // picking "Gradient (Linear, default)". But `legacyFieldsForFill` only
  // commits the legacy currentBg/segmentBgColor fields once `fill.gradient.
  // stops` is a real, non-empty array (by design — see legacyFillFields.js's
  // "never clobber on tab switch" guard, still correct for Image/GIF/Pattern,
  // which show no default until the user makes a concrete choice). Nothing
  // upstream ever seeded that default into `fill`, so the plain act of
  // clicking the tab dispatched only `{type:"gradient"}` — the preview lied:
  // save + reload silently reverted the segment to its previous solid color
  // (BUGS.md gordon-0925-P1-browser-use-full-feature-matrix, current_bg:0).
  // Seed the SAME default the preview already shows so the commit matches
  // what the user sees, exactly like the Color tab's own unconditional
  // "assert the canonical state" write just below.
  // T22.181: the seed is built FROM the Segment's picked color (color -> tint),
  // not the generic peach default, so Color -> Gradient keeps the palette.
  const setType = (type) => {
    if (type === "gradient" && !(fill.gradient && Array.isArray(fill.gradient.stops) && fill.gradient.stops.length > 0)) {
      onChange({ ...fill, type, gradient: gradientStateFromColor(fill.color) });
      return;
    }
    onChange({ ...fill, type });
  };

  return (
    <section style={styles.panel} aria-label={`${segmentName} fill`}>
      <h2 style={styles.h}>{heading}</h2>
      <p style={styles.sub}>{subheading}</p>

      {/* fill-TYPE selector: real ARIA tablist */}
      <div role="tablist" aria-label="Fill type" style={styles.tablist}>
        {FILL_TYPES.map((t) => {
          const active = fill.type === t;
          return (
            <button
              key={t}
              role="tab"
              aria-selected={active}
              tabIndex={active ? 0 : -1}
              style={styles.tab(active)}
              onClick={() => setType(t)}
              data-fill-tab={t}
            >
              {FILL_TYPE_LABEL[t]}
            </button>
          );
        })}
      </div>

      {/* live preview swatch (what the Segment renders) */}
      <div style={animateSwatch ? { ...styles.previewWrap, overflow: "hidden" } : styles.previewWrap}>
        <div
          ref={animateSwatch ? fillAnim.ref : undefined}
          style={
            animateSwatch
              ? { ...styles.preview(bg), transformOrigin: "center", willChange: "transform" }
              : styles.preview(bg)
          }
          role="img"
          aria-label={`${segmentName} fill preview: ${FILL_TYPE_LABEL[fill.type]}`}
          data-fill-preview
          {...(animateSwatch
            ? { "data-fill-animating": fillAnim.running ? "true" : "false" }
            : {})}
        />
        {fill.type === "gif" && !open && <span style={styles.chip}>GIF</span>}
        {fill.type === "gif" && reducedMotion && (
          <span style={styles.hint} data-reduced-motion>Reduced motion: poster frame</span>
        )}
      </div>

      {/* per-type sub-panel */}
      {fill.type === "color" &&
        (colorPanel !== undefined ? (
          <div data-subpanel="color">{colorPanel}</div>
        ) : (
          <div data-subpanel="color">
            <div style={styles.swatchRow(swatchPx)} role="group" aria-label="Segment color">
              {PRESET_COLORS.map((c) => (
                <button
                  key={c}
                  style={styles.swatch(c, (fill.color || "").toUpperCase() === c.toUpperCase(), swatchPx)}
                  aria-label={`Color ${c}`}
                  aria-pressed={(fill.color || "").toUpperCase() === c.toUpperCase()}
                  onClick={() => onChange({ ...fill, type: "color", color: c })}
                />
              ))}
            </div>
          </div>
        ))}

      {fill.type === "gradient" && (
        <p style={styles.hint} data-subpanel="gradient">
          Opens the Gradient builder (Linear / Radial / Conic). Conic centres on the Segment and loops
          seamlessly - no hard seam.
        </p>
      )}

      {fill.type === "pattern" && (
        <div data-subpanel="pattern">
          <p style={styles.hint}>
            Tinted SVG pattern with foreground / background colors. Pick a motif, then tune size and
            colors.
          </p>
          <React.Suspense fallback={<p style={styles.hint}>Loading picker…</p>}>
            <BackgroundPatternPanel
              value={(fill.pattern && fill.pattern.css) || ""}
              onApply={applyPattern}
              segmentColor={fill.color}
            />
          </React.Suspense>
        </div>
      )}

      {fill.type === "image" && (
        <div data-subpanel="image">
          <p style={styles.hint}>
            Upload or pick an image; it fills the Segment (cover). Use the Segment&apos;s resize /
            position controls to frame it.
          </p>
          <React.Suspense fallback={<p style={styles.hint}>Loading picker…</p>}>
            <MediaSelection
              type="image"
              categories={["Upload", "Embed URL", "Browse", "Recent"]}
              media={media}
              setMedia={setMedia}
              handleChange={applyMedia("image")}
              handleRemove={clearMedia("image")}
              handleTitle={() => {}}
            />
          </React.Suspense>
          {SEGMENT_ARC_FILLS_ENABLED && fill.image && fill.image.url && (
            <FocalZoomControl
              src={fill.image.url}
              focus={fill.image.focus}
              mode={fill.image.mode || "cover"}
              onChange={applyCrop("image")}
            />
          )}
        </div>
      )}

      {fill.type === "gif" && (
        <div data-subpanel="gif">
          <p style={styles.hint}>
            A GIF plays on the Wheel automatically. With many GIFs (or on a low-memory device) it
            plays on hover. Reduced motion keeps a still frame.
          </p>
          <React.Suspense fallback={<p style={styles.hint}>Loading picker…</p>}>
            <MediaSelection
              type="image"
              isGifAllowed
              categories={["Upload", "Embed URL", "Browse", "Recent"]}
              media={media}
              setMedia={setMedia}
              handleChange={applyMedia("gif")}
              handleRemove={clearMedia("gif")}
              handleTitle={() => {}}
            />
          </React.Suspense>
          {SEGMENT_ARC_FILLS_ENABLED && fill.gif && fill.gif.url && (
            <FocalZoomControl
              src={fill.gif.poster || fill.gif.url}
              focus={fill.gif.focus}
              mode={fill.gif.mode || "cover"}
              onChange={applyCrop("gif")}
            />
          )}
        </div>
      )}
    </section>
  );
};

export default SegmentFillPanel;
